import { SETTINGS_ROUTES } from './settingsRoutes'
import { canAccessSettingsRoute, type SettingsAccess } from './settingsAccess'

export type SettingsSearchEntry = {
    id: string
    label: string
    description?: string
    href: string
    section: 'brand' | 'shopper' | 'integrations' | 'account'
    group: string
    keywords: string[]
}

export const SETTINGS_SEARCH_INDEX: SettingsSearchEntry[] = [
    {
        id: 'brand-identity',
        label: 'Identity',
        description: 'Logo, icon, and display name',
        href: SETTINGS_ROUTES.brand.identity,
        section: 'brand',
        group: 'Brand Center',
        keywords: ['logo', 'icon', 'display name', 'brand', 'identity'],
    },
    {
        id: 'brand-colors',
        label: 'Colors & Styles',
        description: 'Brand colors and button radius',
        href: SETTINGS_ROUTES.brand.colorsAndStyles,
        section: 'brand',
        group: 'Brand Center',
        keywords: [
            'colors',
            'colour',
            'primary',
            'accent',
            'cta',
            'button radius',
            'border radius',
            'styles',
        ],
    },
    {
        id: 'brand-typography',
        label: 'Typography',
        description: 'Fonts and typography role overrides',
        href: SETTINGS_ROUTES.brand.typography,
        section: 'brand',
        group: 'Brand Center',
        keywords: [
            'fonts',
            'font',
            'typeface',
            'typography',
            'role overrides',
            'custom fonts',
        ],
    },
    {
        id: 'brand-voice',
        label: 'Brand Guidelines',
        description: 'AI brand guidelines and tone',
        href: SETTINGS_ROUTES.brand.brandVoice,
        section: 'brand',
        group: 'Brand Center',
        keywords: [
            'voice',
            'guidelines',
            'tone',
            'ai',
            'brand voice',
            'brand guidelines',
            'writing',
        ],
    },
    {
        id: 'shopper-promotions',
        label: 'Promotions',
        href: SETTINGS_ROUTES.shopper.promotions,
        section: 'shopper',
        group: 'Promotions & messaging',
        keywords: [
            'promotions',
            'discount',
            'sale',
            'offer',
            'precedence',
            'override',
            'storefront promotions',
        ],
    },
    {
        id: 'shopper-benefits',
        label: 'Benefits',
        href: SETTINGS_ROUTES.shopper.benefits,
        section: 'shopper',
        group: 'Promotions & messaging',
        keywords: [
            'benefits',
            'value props',
            'trust badges',
            'button',
            'add to cart',
            'buy now',
            'checkout',
            'cta copy',
        ],
    },
    {
        id: 'shopper-banners',
        label: 'Banners',
        href: SETTINGS_ROUTES.shopper.banners,
        section: 'shopper',
        group: 'Promotions & messaging',
        keywords: ['banners', 'promotional banner', 'announcement'],
    },
    {
        id: 'shopper-free-shipping',
        label: 'Free shipping',
        href: SETTINGS_ROUTES.shopper.freeShipping,
        section: 'shopper',
        group: 'Promotions & messaging',
        keywords: ['free shipping', 'shipping threshold', 'delivery'],
    },
    {
        id: 'shopper-gift-with-purchase',
        label: 'Gift with purchase',
        description:
            'Free gifts added when a trigger product or spend threshold is met',
        href: SETTINGS_ROUTES.shopper.giftWithPurchase,
        section: 'shopper',
        group: 'Promotions & messaging',
        keywords: [
            'gift',
            'gift with purchase',
            'gwp',
            'free gift',
            'gift promotion',
            'trigger product',
        ],
    },
    {
        id: 'shopper-store-general',
        label: 'Store general',
        href: SETTINGS_ROUTES.shopper.storeGeneral,
        section: 'shopper',
        group: 'Store & checkout',
        keywords: [
            'currency',
            'symbol',
            'product availability',
            'out of stock',
            'hide unavailable',
            'cart',
            'order summary',
            'expand',
        ],
    },
    {
        id: 'shopper-subscriptions',
        label: 'Subscriptions',
        href: SETTINGS_ROUTES.shopper.subscriptions,
        section: 'shopper',
        group: 'Store & checkout',
        keywords: ['subscription', 'selling plan', 'recharge', 'subscribe'],
    },
    {
        id: 'shopper-payments',
        label: 'Payments',
        href: SETTINGS_ROUTES.shopper.payments,
        section: 'shopper',
        group: 'Store & checkout',
        keywords: ['shop pay', 'shopify pay', 'payment', 'wallet'],
    },
    {
        id: 'shopper-privacy',
        label: 'Privacy & redirects',
        href: SETTINGS_ROUTES.shopper.privacyAndRedirects,
        section: 'shopper',
        group: 'Store & checkout',
        keywords: [
            'consent',
            'cookie',
            'privacy',
            'gdpr',
            'desktop redirect',
            'mobile redirect',
        ],
    },
    {
        id: 'integrations-pixels',
        label: 'Tracking pixels',
        href: SETTINGS_ROUTES.integrations.pixels,
        section: 'integrations',
        group: 'Integrations',
        keywords: [
            'pixels',
            'tracking',
            'analytics',
            'meta',
            'facebook',
            'ga4',
            'gtm',
            'tiktok',
            'klaviyo',
            'hyros',
            'converge',
        ],
    },
    {
        id: 'integrations-reviews',
        label: 'Reviews',
        href: SETTINGS_ROUTES.integrations.reviews,
        section: 'integrations',
        group: 'Integrations',
        keywords: [
            'reviews',
            'yotpo',
            'okendo',
            'stamped',
            'judge.me',
            'loox',
            'csv',
            'social proof',
        ],
    },
    {
        id: 'account-team',
        label: 'Team',
        href: SETTINGS_ROUTES.account.team,
        section: 'account',
        group: 'Account',
        keywords: ['team', 'users', 'invite', 'roles', 'members'],
    },
    {
        id: 'account-billing',
        label: 'Billing',
        href: SETTINGS_ROUTES.account.billing,
        section: 'account',
        group: 'Account',
        keywords: ['billing', 'payment', 'subscription', 'invoice', 'stripe'],
    },
    {
        id: 'account-domains',
        label: 'Domains',
        href: SETTINGS_ROUTES.account.domains,
        section: 'account',
        group: 'Account',
        keywords: ['domain', 'shopify domain', 'custom domain', 'main domain'],
    },
    {
        id: 'account-advanced',
        label: 'Advanced',
        href: SETTINGS_ROUTES.account.advanced,
        section: 'account',
        group: 'Account',
        keywords: [
            'commerce platform',
            'media import',
            'powered by',
            'crowlink',
            'internal',
        ],
    },
]

export const filterSettingsSearchIndex = (
    access: SettingsAccess,
): SettingsSearchEntry[] =>
    SETTINGS_SEARCH_INDEX.filter((entry) =>
        canAccessSettingsRoute(entry.href, access),
    )
